import { createRequestFilters, ListProviderGuarantorsRequest, ProviderGuarantor } from '@enter/core';
import _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import type { GuarantorGenerator } from './guarantor.generator';
import { GuarantorService } from './guarantor.service';
import { IFeatures, IItemDetail, IItemListing, IItemListingRequest } from './root.store';
import { appendTo, parseItemListingRequest } from './util';

export class GuarantorStore {
    @observable public guarantors: ProviderGuarantor[] = [];

    private features: IFeatures;
    private guarantorGenerator: GuarantorGenerator;
    private guarantorService: GuarantorService = new GuarantorService();

    constructor(features: IFeatures, guarantorGenerator: GuarantorGenerator) {
        this.features = features;
        this.guarantorGenerator = guarantorGenerator;
    }

    @action
    public async loadGuarantorListing(ilr: IItemListingRequest): Promise<IItemListing<ProviderGuarantor>> {
        const lpgr = ilr2lpgr(ilr);
        const listing = await (this.features.isDemo
            ? this.guarantorGenerator.loadGuarantorListing(lpgr)
            : this.guarantorService.fetchGuarantorListing(lpgr));
        if (!listing) {
            throw new Error(`No guarantors available`);
        }
        const { items } = listing;
        runInAction(() => {
            this.guarantors = appendTo<ProviderGuarantor>(this.guarantors, items);
        });
        return listing;
    }

    @action
    public async loadGuarantorDetail(id: string): Promise<IItemDetail<ProviderGuarantor>> {
        const detail = await (this.features.isDemo
            ? this.guarantorGenerator.loadGuarantorDetail(id)
            : this.guarantorService.fetchGuarantorDetail(id));
        if (!detail) {
            throw new Error(`No guarantor items available`);
        }
        return detail;
    }

    @action
    public async loadGuarantor(id: string): Promise<ProviderGuarantor> {
        const detail = await (this.features.isDemo
            ? this.guarantorGenerator.loadGuarantor(id)
            : this.guarantorService.fetchGuarantor(id));
        if (!detail) {
            throw new Error(`No guarantor items available`);
        }
        return detail;
    }
}

// Convert from IItemListingRequest to ListProviderGuarantorsRequest
export const ilr2lpgr = (ilr: IItemListingRequest): ListProviderGuarantorsRequest => {
    const base = parseItemListingRequest(ilr) as ListProviderGuarantorsRequest;
    const filters = createRequestFilters(_.get(ilr, 'filters'), true);
    return {
        ...base,
        ...filters,
    };
};
